import moment from "moment";
import { takeLatest, call, put, select, all } from "redux-saga/effects";
import { createAction } from "redux-actions";
import { api } from "../bookingsApi";
import { stringifyParams, stringify } from "../../../../../utils/stringify";
import { getCommonApiParams } from "../../../../../utils/redux-helpers";

export const SET_LOADING = "sagas/attendance/bookings/SET_LOADING";
export const SET_UPDATES_LOADING = "sagas/attendance/bookings/SET_UPDATES_LOADING";

export const SET_DYNAMIC_FILTERS_STATUS = "sagas/attendance/bookings/SET_DYNAMIC_FILTERS_STATUS";

export const SET_FIRST_EDITED_DYNAMIC_FILTER =
  "sagas/attendance/bookings/SET_FIRST_EDITED_DYNAMIC_FILTER";

export const RESET_DYNAMIC_FILTERS_STATUS =
  "sagas/attendance/bookings/RESET_DYNAMIC_FILTERS_STATUS";

export const SET_FILTERS_DIRTY = "sagas/attendance/bookings/SET_FILTERS_DIRTY";
export const FETCH_CURRENT_USER = "sagas/attendance/bookings/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "sagas/attendance/bookings/FETCH_CURRENT_USER_SUCCESS";
export const FETCH_CHART_SUCCESS = "sagas/attendance/bookings/FETCH_CHART_SUCCESS";
export const FETCH_TABLE_SUCCESS = "sagas/attendance/bookings/FETCH_TABLE_SUCCESS";
export const FETCH_FILTERS_SUCCESS = "sagas/attendance/bookings/FETCH_FILTERS_SUCCESS";
export const REFETCH_FILTERS_SUCCESS = "sagas/attendance/bookings/REFETCH_FILTERS_SUCCESS";
export const UPDATE_CHART = "sagas/attendance/bookings/UPDATE_CHART";

export const SET_DATES = "sagas/attendance/bookings/SET_DATES";
export const SET_DATES_FOR_FUTURE_BOOKING =
  "sagas/attendance/bookings/SET_DATES_FOR_FUTURE_BOOKING";
export const UPDATE_DATES = "sagas/attendance/bookings/UPDATE_DATES";

const RESET_FILTER = "sagas/attendance/bookings/RESET_FILTER";
export const RESET_FILTER_SELECTED = "sagas/attendance/bookings/RESET_FILTER_SELECTED";
export const UPDATE_CUSTOM_RANGE = "sagas/attendance/bookings/UPDATE_CUSTOM_RANGE";
export const UPDATE_GROUP_BY = "sagas/attendance/bookings/UPDATE_GROUP_BY";
export const UPDATE_BOOKING_DATE = "sagas/attendance/bookings/UPDATE_BOOKING_DATE";
export const UPDATE_FUTURE_BOOKINGS = "sagas/attendance/bookings/UPDATE_FUTURE_BOOKINGS";

export const UPDATE_FILTERS = "sagas/attendance/bookings/UPDATE_FILTERS";
export const dynamicFilters = ["staff_ids", "class_uids", "class_time"];

const toggleTypes = {
  booking_date: UPDATE_BOOKING_DATE,
  future_bookings: UPDATE_FUTURE_BOOKINGS
};

function getAll() {
  return [
    fetchData(api.getChart, FETCH_CHART_SUCCESS),
    fetchData(api.getTable, FETCH_TABLE_SUCCESS)
  ];
}

export const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  updateData: createAction(UPDATE_CHART),
  updateDates: createAction(UPDATE_DATES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  updateGroupBy: createAction(UPDATE_GROUP_BY),
  resetFilters: createAction(RESET_FILTER),
  updateBookingDate: createAction(UPDATE_BOOKING_DATE),
  updateFutureBookings: createAction(UPDATE_FUTURE_BOOKINGS),
  updateFilters: createAction(UPDATE_FILTERS)
};

export default function* watchNumberOfVisits() {
  yield takeLatest(FETCH_CURRENT_USER, fetchCurrentUserData);
  yield takeLatest(UPDATE_CHART, updateData);
  yield takeLatest(RESET_FILTER, resetFilters);
  yield takeLatest(UPDATE_DATES, refetchFilters);
  yield takeLatest(UPDATE_CUSTOM_RANGE, refetchFilters);
  yield takeLatest(UPDATE_FILTERS, refetchDynamicFilters);
  yield takeLatest(UPDATE_BOOKING_DATE, refetchFilters);
  yield takeLatest(UPDATE_FUTURE_BOOKINGS, refetchFilters);
}

function* setDates() {
  const endDate = yield select((state) => state.bookings.currentUser.calendarEndDate);
  const startDate = moment(endDate).startOf("month").format("YYYY-MM-DD");

  yield put({
    type: SET_DATES,
    payload: {
      startDate,
      endDate
    }
  });
}

function* setDatesForFutureBookings() {
  const startDate = yield select((state) => state.bookings.currentUser.calendarEndDate);
  const hourlyImport = yield select((state) => state.bookings.currentUser.hourlyImport);
  const currentDate = hourlyImport
    ? startDate
    : moment(startDate).add(1, "days").format("YYYY-MM-DD");
  const endDate = moment(currentDate).add(14, "days").startOf("day").format("YYYY-MM-DD");
  const maxDate = moment(currentDate).add(30, "days").startOf("day").format("YYYY-MM-DD");
  yield put({
    type: SET_DATES_FOR_FUTURE_BOOKING,
    payload: {
      selectedRequestDates: {
        startDate: currentDate,
        endDate
      },
      minDate: currentDate,
      maxDate
    }
  });
}

export function* fetchData(endpoint, action) {
  const commonParams = yield getCommonParams();
  const params = yield getApiParams();

  try {
    const { data } = yield call(endpoint, { ...commonParams, ...params });

    yield put({ type: action, payload: data });
  } catch (err) {
    console.error(err);
  }
}

function* fetchCurrentUserData(data) {
  yield put({
    type: FETCH_CURRENT_USER_SUCCESS,
    payload: data.payload
  });

  yield setDates();

  try {
    yield fetchFilters();
    yield all(getAll());
    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* fetchFilters() {
  const params = yield getCommonParams();

  try {
    const { data } = yield call(api.getFilters, params);
    let { staff_ids, class_uids, class_time, status, group_by } = data;
    let filters = { staff_ids, class_uids, class_time, status, group_by };

    yield put({ type: FETCH_FILTERS_SUCCESS, payload: filters });
  } catch (error) {
    console.error(error);
  }
}

function* getTogglesData(data) {
  const { booking_date, future_bookings } = yield select((state) => state.bookings.selectedFilters);

  if (data.type === toggleTypes.booking_date) {
    return {
      booking_date: data.payload,
      future_bookings
    };
  } else if (data.type === toggleTypes.future_bookings) {
    data.payload ? yield setDatesForFutureBookings() : yield setDates();
    return {
      future_bookings: data.payload,
      booking_date
    };
  }
  return {
    future_bookings,
    booking_date
  };
}

function* refetchFilters(data) {
  const actionType = data.type;
  const togglesData = yield getTogglesData(data);
  const params = yield getCommonParams();

  try {
    yield put({ type: SET_DYNAMIC_FILTERS_STATUS });

    const { data } = yield call(api.getFilters, {
      ...params,
      ...togglesData
    });
    let { staff_ids, class_uids, class_time, status } = data;
    let filters = { staff_ids, class_uids, class_time };

    if (actionType === toggleTypes.future_bookings) {
      filters.status = status;
    }

    yield put({ type: REFETCH_FILTERS_SUCCESS, payload: filters });
    yield put({ type: RESET_DYNAMIC_FILTERS_STATUS });
  } catch (error) {
    console.error(error);
  }
}

function* checkForFirstFilterControl(currentFilterName, data) {
  //first changed dynamic filter makes other dynamic filters static, meaning no request send to backend
  let firstEditedDynamicFilter = yield select((state) => state.bookings.firstEditedDynamicFilter);

  if (!firstEditedDynamicFilter) {
    yield put({ type: SET_FIRST_EDITED_DYNAMIC_FILTER, payload: currentFilterName });
  }

  if (firstEditedDynamicFilter && currentFilterName === firstEditedDynamicFilter) {
    let allOptions = yield select((state) => state.bookings.filters[firstEditedDynamicFilter]);

    //if all options were selected, then  remove first filter control
    if (allOptions.length === data.length) {
      yield put({ type: SET_FIRST_EDITED_DYNAMIC_FILTER, payload: "" });
    }
  }

  return firstEditedDynamicFilter && currentFilterName !== firstEditedDynamicFilter;
}

function* refetchDynamicFilters(info) {
  let { key, data } = info.payload;
  // const dynamicFilters = ["staff", "class_name", "class_time"];

  if (dynamicFilters.includes(key)) {
    let skipRefetch = yield checkForFirstFilterControl(key, data);

    if (skipRefetch) return;

    const params = yield getCommonParams();

    try {
      yield put({ type: SET_DYNAMIC_FILTERS_STATUS, payload: key });

      const res = yield call(api.getFilters, {
        ...params,
        [key]: stringify(data, key == "staff_ids" ? "id" : "name")
      });

      yield put({ type: REFETCH_FILTERS_SUCCESS, payload: res.data });
      yield put({ type: RESET_DYNAMIC_FILTERS_STATUS });
    } catch (error) {
      console.error(error);
    }
  }
}

function* updateData() {
  yield put({ type: SET_FILTERS_DIRTY, payload: false });
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  try {
    yield all(getAll());
    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* resetFilters() {
  yield put({ type: SET_LOADING, payload: true });
  yield setDates();
  yield put({ type: RESET_FILTER_SELECTED });
  try {
    yield fetchFilters();
    yield all(getAll());
    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* getCommonParams() {
  let { organization_id, short_url } = yield select((state) => state.commonReducer.commonProps);

  let { startDate, endDate } = yield select((state) => state.bookings.selectedRequestDates);

  return {
    organization_id,
    short_url,
    start_date: startDate,
    end_date: endDate
  };
}

function* getApiParams() {
  return yield select((state) => {
    const { group_by, staff_ids, status, class_uids, ...rest } = state.bookings.selectedFilters;

    let params = stringifyParams(rest);
    return {
      ...params,
      class_uids: stringify(class_uids, "id"),
      staff_ids: stringify(staff_ids, "id"),
      status: stringify(status, "id"),
      group_by: group_by.id
    };
  });
}
